import { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Send, Loader2, X, Image, Mic, MicOff, Play, Lock
} from 'lucide-react';
import { toast } from 'sonner';

export const MessageInput = ({
  api,
  userId,
  canSend,
  cannotSendReason,
  messages,
  setMessages
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Photo state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ===== TEXT =====
  const handleSend = async () => {
    if (!newMessage.trim() || !canSend) return;
    setSending(true);
    try {
      const res = await api().post(`/direct-messages/send/${userId}`, { content: newMessage.trim(), message_type: 'text' });
      setMessages(prev => [...prev, res.data.dm]);
      setNewMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ===== PHOTO =====
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSendImage = async () => {
    if (!selectedImage || !canSend) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('media_type', 'photo');
      const uploadRes = await api().post('/direct-messages/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await api().post(`/direct-messages/send/${userId}`, { content: '', message_type: 'photo', media_url: uploadRes.data.media_url });
      setMessages(prev => [...prev, res.data.dm]);
      clearImageSelection();
      toast.success('Photo sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send photo');
    }
    setUploadingImage(false);
  };

  const clearImageSelection = () => {
    setSelectedImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ===== VOICE =====
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          if (prev >= 60) { stopRecording(); return 60; }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      toast.error('Could not access microphone.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); recordingIntervalRef.current = null; }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setRecordingDuration(0);
  };

  const handleSendVoice = async () => {
    if (!audioBlob || !canSend) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice.webm');
      formData.append('media_type', 'voice');
      const uploadRes = await api().post('/direct-messages/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await api().post(`/direct-messages/send/${userId}`, { content: '', message_type: 'voice', media_url: uploadRes.data.media_url, duration: recordingDuration });
      setMessages(prev => [...prev, res.data.dm]);
      cancelRecording();
      toast.success('Voice message sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send voice message');
    }
    setSending(false);
  };

  if (!canSend) {
    return (
      <div className="border-t border-border p-4 pb-20 bg-card">
        <div className="text-center py-2">
          <p className="text-muted-foreground text-sm flex items-center justify-center gap-2">
            <Lock className="h-4 w-4" />
            {cannotSendReason || 'You cannot message this user'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border p-4 pb-20 bg-card">
      {/* Image Preview */}
      <AnimatePresence>
        {imagePreview && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="mb-4 max-w-3xl mx-auto">
            <div className="relative inline-block">
              <img src={imagePreview} alt="Preview" className="max-h-40 rounded-lg border border-border" />
              <Button size="icon" variant="destructive" className="absolute -top-2 -right-2 h-6 w-6 rounded-full" onClick={clearImageSelection}>
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="mt-2">
              <Button onClick={handleSendImage} disabled={uploadingImage} className="bg-primary text-black">
                {uploadingImage ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : <><Send className="h-4 w-4 mr-2" /> Send Photo</>}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice Recording UI */}
      <AnimatePresence>
        {(isRecording || audioBlob) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="mb-4 max-w-3xl mx-auto">
            <div className="flex items-center gap-4 p-4 bg-black/50 border border-border rounded-lg">
              {isRecording ? (
                <>
                  <div className="flex items-center gap-2 flex-1">
                    <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 font-medium">Recording...</span>
                    <span className="text-white ml-2">{formatDuration(recordingDuration)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={cancelRecording}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  <Button size="sm" className="bg-primary text-black" onClick={stopRecording}><MicOff className="h-4 w-4 mr-1" /> Stop</Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-1">
                    <Button size="icon" variant="ghost" onClick={() => { new Audio(audioUrl).play(); }}><Play className="h-4 w-4" /></Button>
                    <div className="flex-1 h-1 bg-white/20 rounded-full" />
                    <span className="text-white">{formatDuration(recordingDuration)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={cancelRecording}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  <Button size="sm" className="bg-primary text-black" onClick={handleSendVoice} disabled={sending}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Text Input Bar */}
      {!imagePreview && !isRecording && !audioBlob && (
        <div className="flex gap-2 max-w-3xl mx-auto">
          <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" ref={fileInputRef} />
          <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} className="text-muted-foreground hover:text-primary" title="Send photo">
            <Image className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={startRecording} className="text-muted-foreground hover:text-primary" title="Record voice message">
            <Mic className="h-5 w-5" />
          </Button>
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 bg-black border-white/20"
            maxLength={1000}
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-primary text-black">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
};
